import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  MessageSquare, Clock, CheckCircle2, CreditCard, Camera,
  FileText, PenLine, CalendarClock, Pin, PinOff, Trash2,
  Loader2, Send, Paperclip, Eye, Download, ArrowRightLeft
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { downloadFile } from '@/lib/fileUtils';

const TYPE_CONFIG = {
  note:                  { icon: MessageSquare,   color: 'text-blue-600',   bg: 'bg-blue-100',   label: 'Заметка' },
  status_change:         { icon: ArrowRightLeft,  color: 'text-indigo-600', bg: 'bg-indigo-100',  label: 'Статус' },
  payment_status_change: { icon: CreditCard,      color: 'text-emerald-600',bg: 'bg-emerald-100', label: 'Оплата' },
  assignment_change:     { icon: CheckCircle2,    color: 'text-purple-600', bg: 'bg-purple-100',  label: 'Назначение' },
  photo_uploaded:        { icon: Camera,          color: 'text-amber-600',  bg: 'bg-amber-100',   label: 'Фото' },
  document_generated:    { icon: FileText,        color: 'text-teal-600',   bg: 'bg-teal-100',    label: 'Документ' },
  document_signed:       { icon: PenLine,         color: 'text-green-600',  bg: 'bg-green-100',   label: 'Подпись' },
  payment_received:      { icon: CreditCard,      color: 'text-green-600',  bg: 'bg-green-100',   label: 'Платёж' },
  reschedule:            { icon: CalendarClock,    color: 'text-orange-600', bg: 'bg-orange-100',  label: 'Перенос' },
  system:                { icon: Clock,           color: 'text-gray-500',   bg: 'bg-gray-100',    label: 'Система' },
};

function ActivityItem({ activity, onPin, onDelete, canManage }) {
  const cfg = TYPE_CONFIG[activity.activity_type] || TYPE_CONFIG.system;
  const Icon = cfg.icon;
  const isNote = activity.activity_type === 'note';
  
  // SEC-01 FIX: Secure download without token in URL
  const handleDownload = async (file) => {
    try {
      await downloadFile(file.id, file.original_filename || 'file');
    } catch (err) {
      toast.error('Ошибка загрузки файла');
    }
  };

  return (
    <div className={cn(
      "relative flex gap-3 py-3 px-3 rounded-lg transition-colors group",
      activity.pinned && "bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200/50",
      !activity.pinned && "hover:bg-muted/30"
    )} data-testid={`activity-${activity.id}`}>
      {/* Icon */}
      <div className={cn("w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5", cfg.bg)}>
        <Icon className={cn("h-4 w-4", cfg.color)} />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-sm whitespace-pre-wrap break-words">{activity.content}</p>
            {/* Attachments */}
            {activity.attachments?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {activity.attachments.map(f => (
                  <button key={f.id} onClick={() => handleDownload(f)}
                     className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-muted hover:bg-muted/80 transition-colors cursor-pointer">
                    <Paperclip className="h-3 w-3" />{f.original_filename?.slice(0, 25) || 'file'}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          {canManage && isNote && (
            <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
              <Button size="icon" variant="ghost" className="h-6 w-6"
                onClick={() => onPin(activity.id)}
                title={activity.pinned ? 'Открепить' : 'Закрепить'}
                data-testid={`pin-${activity.id}`}>
                {activity.pinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
              </Button>
              <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive hover:text-destructive"
                onClick={() => onDelete(activity.id)}
                title="Удалить"
                data-testid={`delete-${activity.id}`}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>

        {/* Meta */}
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0 h-4", cfg.color)}>{cfg.label}</Badge>
          {activity.author_name && <span className="text-xs text-muted-foreground">{activity.author_name}</span>}
          {!activity.is_internal && <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4"><Eye className="h-2.5 w-2.5 mr-0.5" />Клиенту</Badge>}
          {activity.pinned && <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 text-amber-600 border-amber-300"><Pin className="h-2.5 w-2.5 mr-0.5" />Закреплено</Badge>}
          <span className="text-[10px] text-muted-foreground ml-auto tabular-nums">
            {activity.created_at ? new Date(activity.created_at).toLocaleString('ru', {day:'2-digit',month:'2-digit',year:'2-digit',hour:'2-digit',minute:'2-digit'}) : ''}
          </span>
        </div>
      </div>
    </div>
  );
}

function NoteInput({ orderId, onAdded }) {
  const { api } = useAuth();
  const [text, setText] = useState('');
  const [isInternal, setIsInternal] = useState(true);
  const [sending, setSending] = useState(false);
  const [files, setFiles] = useState([]);
  const fileRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim() && !files.length) return;
    setSending(true);
    const fd = new FormData();
    fd.append('content', text.trim());
    fd.append('is_internal', isInternal);
    files.forEach(f => fd.append('files', f));
    try {
      await api.post(`/orders/${orderId}/notes`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setText('');
      setFiles([]);
      onAdded?.();
    } catch { toast.error('Ошибка отправки'); }
    finally { setSending(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="border rounded-lg p-3 space-y-2" data-testid="note-input-form">
      <Textarea
        value={text} onChange={e => setText(e.target.value)}
        placeholder="Добавить заметку..."
        className="min-h-[60px] resize-none text-sm"
        data-testid="note-input-text"
      />
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {files.map((f, i) => (
            <Badge key={i} variant="secondary" className="text-xs gap-1">
              <Paperclip className="h-3 w-3" />{f.name.slice(0, 20)}
              <button type="button" onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} className="ml-1 hover:text-destructive">&times;</button>
            </Badge>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <Button type="button" size="icon" variant="ghost" className="h-7 w-7"
            onClick={() => fileRef.current?.click()} data-testid="note-attach-btn">
            <Paperclip className="h-4 w-4" />
          </Button>
          <input ref={fileRef} type="file" multiple className="hidden" onChange={e => { setFiles(prev => [...prev, ...Array.from(e.target.files)]); e.target.value = ''; }} />
          <div className="flex items-center gap-1.5">
            <Switch id="internal" checked={!isInternal} onCheckedChange={v => setIsInternal(!v)} data-testid="note-visibility-switch" />
            <Label htmlFor="internal" className="text-xs text-muted-foreground cursor-pointer">
              {isInternal ? 'Только для команды' : 'Видно клиенту'}
            </Label>
          </div>
        </div>
        <Button type="submit" size="sm" disabled={sending || (!text.trim() && !files.length)} data-testid="note-submit-btn">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </form>
  );
}

export function OrderTimeline({ orderId, canManage = true }) {
  const { api } = useAuth();
  const [activities, setActivities] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback(() => {
    if (!orderId) return;
    setLoading(true);
    const params = new URLSearchParams({ limit: '100' });
    if (filter !== 'all') params.set('activity_type', filter);
    api.get(`/orders/${orderId}/activity?${params}`).then(res => {
      setActivities(res.data.activities || []);
      setTotal(res.data.total || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [orderId, filter, api]);

  useEffect(() => { load(); }, [load]);

  const handlePin = async (id) => {
    try { await api.put(`/order-activities/${id}/pin`); load(); } catch { toast.error('Ошибка'); }
  };
  const handleDelete = async (id) => {
    try { await api.delete(`/order-activities/${id}`); load(); toast.success('Удалено'); } catch { toast.error('Ошибка'); }
  };

  const filters = [
    { key: 'all', label: 'Все' },
    { key: 'note', label: 'Заметки' },
    { key: 'status_change', label: 'Статусы' },
    { key: 'payment_received', label: 'Платежи' },
    { key: 'photo_uploaded', label: 'Фото' },
    { key: 'document_generated', label: 'Документы' },
  ];

  return (
    <div className="space-y-3" data-testid="order-timeline">
      {canManage && <NoteInput orderId={orderId} onAdded={load} />}
      <div className="flex items-center gap-1 overflow-x-auto py-1">
        {filters.map(f => (
          <Button key={f.key} size="sm" variant={filter === f.key ? 'default' : 'ghost'}
            className="text-xs h-7 px-2 shrink-0" onClick={() => setFilter(f.key)}
            data-testid={`filter-${f.key}`}>
            {f.label}
          </Button>
        ))}
        <span className="ml-auto text-xs text-muted-foreground tabular-nums shrink-0">{total}</span>
      </div>
      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : !activities.length ? (
        <div className="text-center py-6 text-muted-foreground"><MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-20" /><p className="text-sm">Нет записей</p></div>
      ) : (
        <div className="space-y-1 relative before:absolute before:left-[19px] before:top-4 before:bottom-4 before:w-px before:bg-border">
          {activities.map(a => (
            <ActivityItem key={a.id} activity={a} onPin={handlePin} onDelete={handleDelete} canManage={canManage} />
          ))}
        </div>
      )}
    </div>
  );
}

export function ClientActivityTab({ clientId }) {
  const { api } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    api.get(`/clients/${clientId}/activity?limit=100`).then(res => {
      setActivities(res.data.activities || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [clientId, api]);

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  if (!activities.length) return <div className="text-center py-6 text-muted-foreground"><Clock className="h-8 w-8 mx-auto mb-2 opacity-20" /><p className="text-sm">Нет активности</p></div>;

  return (
    <div className="space-y-1" data-testid="client-activity-tab">
      {activities.map(a => {
        const cfg = TYPE_CONFIG[a.activity_type] || TYPE_CONFIG.system;
        const Icon = cfg.icon;
        return (
          <div key={a.id} className="flex gap-3 py-2 px-2 rounded-lg hover:bg-muted/30">
            <div className={cn("w-7 h-7 rounded-full flex items-center justify-center shrink-0", cfg.bg)}>
              <Icon className={cn("h-3.5 w-3.5", cfg.color)} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm">{a.content}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0 h-4", cfg.color)}>{cfg.label}</Badge>
                {a.order_title && <span className="text-xs text-muted-foreground truncate">{a.order_title}</span>}
                <span className="text-[10px] text-muted-foreground ml-auto tabular-nums">
                  {a.created_at ? new Date(a.created_at).toLocaleDateString('ru') : ''}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
